import { WarningCircle } from '@phosphor-icons/react'
import type { ColumnMapping, ColumnMappingField, SourceType } from '../../types'

export interface MappingSheetOption {
  name: string
  rowCount: number | null
}

export interface MappingPreview {
  headers: readonly string[]
  rows: readonly (readonly unknown[])[]
  sourceRowNumbers?: readonly number[]
}

interface MappingPanelProps {
  mapping: ColumnMapping
  onHeaderRowChange: (row: number) => void
  onMappingChange: (field: ColumnMappingField, column: number | null) => void
  onSheetChange: (sheet: string) => void
  preview: MappingPreview
  selectedSheet: string
  sheets: readonly MappingSheetOption[]
  sourceType: SourceType
  warning: string | null
}

const mappingFields: ReadonlyArray<{
  field: ColumnMappingField
  label: string
  required: boolean
}> = [
  { field: 'itemCodeColumn', label: '项目编码', required: false },
  { field: 'itemNameColumn', label: '项目名称', required: true },
  { field: 'itemDescriptionColumn', label: '项目特征', required: false },
  { field: 'unitColumn', label: '单位', required: true },
  { field: 'quantityColumn', label: '工程量', required: true },
  { field: 'unitPriceColumn', label: '综合单价', required: true },
  { field: 'totalPriceColumn', label: '合价', required: false },
]

function displayCell(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'object') return '[复杂单元格]'
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value)
  }
  if (typeof value === 'symbol') return value.description ?? '[符号]'
  return '[未知值]'
}

export function isMappingReady(mapping: ColumnMapping): boolean {
  const coreReady =
    mapping.headerRow !== null &&
    mapping.itemNameColumn !== null &&
    mapping.unitColumn !== null &&
    mapping.quantityColumn !== null &&
    mapping.unitPriceColumn !== null
  const identityReady = mapping.itemCodeColumn !== null || mapping.itemDescriptionColumn !== null
  const amountReady = mapping.totalPriceColumn !== null || mapping.quantityColumn !== null
  return coreReady && identityReady && amountReady
}

export function MappingPanel({
  mapping,
  onHeaderRowChange,
  onMappingChange,
  onSheetChange,
  preview,
  selectedSheet,
  sheets,
  sourceType,
  warning,
}: MappingPanelProps) {
  const sourceLabel = sourceType === 'baseline' ? '基准版' : '对比版'
  const maxPreviewColumns = Math.min(preview.headers.length, 8)

  return (
    <section className="mapping-panel" aria-labelledby={`${sourceType}-mapping-title`}>
      <div className="mapping-panel__heading">
        <div>
          <span className={`source-chip source-chip--${sourceType}`}>{sourceLabel}</span>
          <h3 id={`${sourceType}-mapping-title`}>工作表与字段映射</h3>
        </div>
        <span className={isMappingReady(mapping) ? 'mapping-state is-ready' : 'mapping-state'}>
          {isMappingReady(mapping) ? '核心字段已就绪' : '请补全核心字段'}
        </span>
      </div>

      <div className="mapping-controls mapping-controls--primary">
        <label>
          <span>工作表</span>
          <select value={selectedSheet} onChange={(event) => onSheetChange(event.target.value)}>
            {sheets.map((sheet) => (
              <option key={sheet.name} value={sheet.name}>
                {sheet.name}
                {sheet.rowCount === null ? '' : `（约 ${sheet.rowCount} 行）`}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>标题行</span>
          <select
            value={mapping.headerRow ?? 1}
            onChange={(event) => onHeaderRowChange(Number(event.target.value))}
          >
            {Array.from({ length: 30 }, (_, index) => index + 1).map((row) => (
              <option key={row} value={row}>
                第 {row} 行
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mapping-grid">
        {mappingFields.map(({ field, label, required }) => (
          <label key={field}>
            <span>
              {label}
              {required ? <em>必需</em> : <small>可选</small>}
            </span>
            <select
              data-testid={`${sourceType}-${field}`}
              value={mapping[field] ?? ''}
              onChange={(event) =>
                onMappingChange(
                  field,
                  event.target.value === '' ? null : Number(event.target.value),
                )
              }
            >
              <option value="">不映射</option>
              {preview.headers.map((header, index) => (
                <option key={`${index}-${header}`} value={index}>
                  {String.fromCharCode(65 + (index % 26))} · {header || `第 ${index + 1} 列`}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>

      {warning ? (
        <p className="inline-message inline-message--warning" role="status">
          <WarningCircle aria-hidden="true" weight="fill" />
          {warning}
        </p>
      ) : null}

      <div className="preview-block">
        <div className="preview-block__heading">
          <strong>数据预览</strong>
          <span>前 {Math.min(preview.rows.length, 10)} 条数据，仅用于确认映射</span>
        </div>
        <div className="preview-table-wrap" tabIndex={0} aria-label={`${sourceLabel}数据预览`}>
          <table className="preview-table">
            <thead>
              <tr>
                <th>来源行</th>
                {preview.headers.slice(0, maxPreviewColumns).map((header, index) => (
                  <th key={`${index}-${header}`}>{header || `第 ${index + 1} 列`}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.rows.slice(0, 10).map((row, rowIndex) => (
                <tr key={preview.sourceRowNumbers?.[rowIndex] ?? rowIndex}>
                  <td>{preview.sourceRowNumbers?.[rowIndex] ?? rowIndex + 1}</td>
                  {row.slice(0, maxPreviewColumns).map((cell, columnIndex) => (
                    <td key={columnIndex} title={displayCell(cell)}>
                      {displayCell(cell)}
                    </td>
                  ))}
                </tr>
              ))}
              {preview.rows.length === 0 ? (
                <tr>
                  <td colSpan={maxPreviewColumns + 1} className="preview-table__empty">
                    当前工作表没有可预览的数据。
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}
